import React from "react";

export interface PromptOptions {
  title: string;
  message?: React.ReactNode;
  label?: React.ReactNode;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** Disable confirm until the field is non-empty. */
  required?: boolean;
  /** Type-to-confirm: disable confirm until the input exactly equals this. */
  matchText?: string;
}

type Resolver = (value: string | null) => void;

interface PromptContextValue {
  prompt: (options: PromptOptions) => Promise<string | null>;
}

const PromptContext = React.createContext<PromptContextValue | null>(null);

/**
 * Promise-based text-input modal. Replaces the native window.prompt().
 *
 *   const prompt = usePrompt();
 *   const reason = await prompt({ title: "Reason?", required: true });
 *   if (reason === null) return; // user cancelled
 *
 * Resolves with the entered string, or null if cancelled (Escape / backdrop /
 * Cancel). Naming the hook result `prompt` deliberately shadows window.prompt
 * so a stray native call is hard to write by accident.
 */
export function PromptProvider(props: { children: React.ReactNode }) {
  const [state, setState] = React.useState<{ options: PromptOptions; resolve: Resolver } | null>(null);
  const [value, setValue] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  const prompt = React.useCallback((options: PromptOptions) => {
    setValue(options.defaultValue ?? "");
    return new Promise<string | null>((resolve) => setState({ options, resolve }));
  }, []);

  const close = (result: string | null) => {
    if (!state) return;
    state.resolve(result);
    setState(null);
  };

  React.useEffect(() => {
    if (!state) return;
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close(null);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const o = state?.options;
  const blocked =
    !!o &&
    (((o.required ?? false) && value.trim().length === 0) ||
      (o.matchText !== undefined && value !== o.matchText));

  const submit = () => {
    if (blocked) return;
    close(value);
  };

  return (
    <PromptContext.Provider value={{ prompt }}>
      {props.children}
      {o ? (
        <div className="modal-backdrop" onClick={() => close(null)}>
          <div className="modal" role="dialog" aria-modal onClick={(e) => e.stopPropagation()}>
            <h2>{o.title}</h2>
            {o.message ? <p>{o.message}</p> : null}
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
            >
              <label>
                {o.label ?? "Value"}
                <input
                  ref={inputRef}
                  type="text"
                  value={value}
                  placeholder={o.placeholder}
                  onChange={(e) => setValue(e.target.value)}
                />
              </label>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => close(null)}>
                  {o.cancelLabel ?? "Cancel"}
                </button>
                <button
                  type="submit"
                  className={`btn ${o.danger ? "btn-danger" : "btn-primary"}`}
                  disabled={blocked}
                >
                  {o.confirmLabel ?? "OK"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </PromptContext.Provider>
  );
}

export function usePrompt() {
  const ctx = React.useContext(PromptContext);
  if (!ctx) {
    throw new Error("usePrompt must be used within a <PromptProvider> (mounted in App.tsx).");
  }
  return ctx.prompt;
}
